import { useCallback, useEffect, useRef, useState } from 'react';

export type AudioControls = {
  audioRef: React.RefObject<HTMLAudioElement | null>;
  isPlaying: boolean;
  isReady: boolean;
  isBuffering: boolean;
  currentTime: number;
  duration: number;
  progress: number;
  srcAttached: boolean;
  togglePlay: () => void;
  handleRewind: () => void;
  handleForward: () => void;
  seekTo: (ratio: number) => void;
};

export function useAudioPlayer({
  rewindSeconds = 10,
  forwardSeconds = 15,
  knownDuration,
}: {
  rewindSeconds?: number;
  forwardSeconds?: number;
  knownDuration?: number;
}) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isReady, setIsReady] = useState(knownDuration != null && knownDuration > 0);
  const [isBuffering, setIsBuffering] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(knownDuration ?? 0);
  const [srcAttached, setSrcAttached] = useState(knownDuration == null || knownDuration <= 0);

  // Track the target time during seeks so the UI doesn't flash to 0:00
  const seekTargetRef = useRef<number | null>(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTimeUpdate = () => {
      // While seeking, hold the display at the target time until the browser catches up
      if (seekTargetRef.current != null) {
        if (Math.abs(audio.currentTime - seekTargetRef.current) < 1) {
          seekTargetRef.current = null;
        } else {
          setCurrentTime(seekTargetRef.current);
          return;
        }
      }
      setCurrentTime(audio.currentTime);
    };
    const onLoadedMetadata = () => {
      setDuration(audio.duration || 0);
      setIsReady(true);
    };
    const onPlay = () => setIsPlaying(true);
    const onPause = () => {
      setIsPlaying(false);
      setIsBuffering(false);
    };
    const onEnded = () => {
      setIsPlaying(false);
      setIsBuffering(false);
    };
    const onWaiting = () => setIsBuffering(true);
    const onCanPlay = () => setIsBuffering(false);
    const onPlaying = () => setIsBuffering(false);
    const onSeeked = () => {
      seekTargetRef.current = null;
      setCurrentTime(audio.currentTime);
      setIsBuffering(false);
    };
    const onError = () => {
      setIsBuffering(false);
    };

    // Check if metadata already loaded (race condition fix)
    // readyState >= 1 means HAVE_METADATA or higher
    if (audio.readyState >= 1) {
      onLoadedMetadata();
    }

    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('waiting', onWaiting);
    audio.addEventListener('canplay', onCanPlay);
    audio.addEventListener('playing', onPlaying);
    audio.addEventListener('seeked', onSeeked);
    audio.addEventListener('error', onError);

    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('waiting', onWaiting);
      audio.removeEventListener('canplay', onCanPlay);
      audio.removeEventListener('playing', onPlaying);
      audio.removeEventListener('seeked', onSeeked);
      audio.removeEventListener('error', onError);
    };
  }, []);

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (!audio.paused) {
      audio.pause();
      return;
    }

    // First user interaction: allow source element to render, then start loading
    if (!srcAttached) {
      setSrcAttached(true);
    }

    setIsBuffering(true);
    audio
      .play()
      .then(() => {
        setIsPlaying(true);
        setIsBuffering(false);
      })
      .catch(() => {
        setIsPlaying(false);
        setIsBuffering(false);
      });
  }, [srcAttached]);

  const handleRewind = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !srcAttached) return;
    const target = Math.max(0, audio.currentTime - rewindSeconds);
    seekTargetRef.current = target;
    setCurrentTime(target);
    audio.currentTime = target;
  }, [rewindSeconds, srcAttached]);

  const handleForward = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !srcAttached) return;
    const target = Math.min(audio.duration || 0, audio.currentTime + forwardSeconds);
    seekTargetRef.current = target;
    setCurrentTime(target);
    audio.currentTime = target;
  }, [forwardSeconds, srcAttached]);

  const seekTo = useCallback(
    (ratio: number) => {
      const audio = audioRef.current;
      if (!audio || !duration || !srcAttached) return;
      const target = ratio * duration;
      seekTargetRef.current = target;
      setCurrentTime(target);
      audio.currentTime = target;
    },
    [duration, srcAttached],
  );

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return {
    audioRef,
    isPlaying,
    isReady,
    isBuffering,
    currentTime,
    duration,
    progress,
    srcAttached,
    togglePlay,
    handleRewind,
    handleForward,
    seekTo,
  };
}
